import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import sortBy from 'lodash.sortby';

import { translate } from '../../../common/translation/translate';
import { GearIcon } from '../../common/icons/gearIcon';
import { reduxConnect } from '../../../common';
import { FavoriteLocationIcon } from './favoriteLocationIcon';
import { LocationAlert } from './locationAlert';
import { getLocationUrl, getRoutePath } from './utilities';
import { showManagedLocationsPopup } from '../../../common/actions/manageLocationsPopupActions';
import { defaultStationName } from '../../../common/utilities/constants';

const defaultMapZoom = 7;

const mapStoreStateToProps = (storeState) => {
    const { manageLocationsPopup, appState } =  storeState;
    const favoriteLocations = storeState?.favoriteLocations?.favoriteLocations;
    const { routePath, pageId, defaultMapLayerId } = appState?.appState || {};
    const locRoutePath = getRoutePath(routePath);
    const { showManagedLocations } = manageLocationsPopup || {};

    return {
        favoriteLocations,
        routePath: locRoutePath,
        showManagedLocations,
        pageId,
        defaultMapLayerId
    };
};

const mapDispatchToProps = {
    showManagedLocationsPopup
};

export const FavoriteLocationDropdown = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({ 
    favoriteLocations=[],
    pageId,
    mapCurrentLayerId, 
    routePath,
    defaultMapLayerId, 
    close, 
    showManagedLocations, 
    showManagedLocationsPopup }) => {
    const [sortedFavoriteLocations, setSortedFavoriteLocations] = useState(favoriteLocations);

    const manageLocationsClick = useCallback(()=>{
        showManagedLocationsPopup(!showManagedLocations);
        close();
    }, [showManagedLocations, routePath]);

    useEffect(() => {
        setSortedFavoriteLocations(sortBy(favoriteLocations, 'Position'));
    }, [favoriteLocations]);

    return (
        <Container>
            <Header>
                {translate('favoriteLocations')}
            </Header>
            <LocationsList id='favoriteLocationsList'>
                {sortedFavoriteLocations.map(favoriteLocation=> {
                    const stationRouteString = favoriteLocation?.Location?.Station?.RouteString;
                    const locationRouteString = favoriteLocation?.Location?.Locality?.RouteString || favoriteLocation?.Location?.Locality?.SlugName;

                    // we need to ensure stations routeString is included in url for locations that are favorited, without which clicking on saved favorited locations from
                    // drop down list will take users back to default location + station
                    // e.g https://www.weatherbug.com/weather-forecast/now/boston-ma-02111?station=3:CMCFD
                    const { Latitude, Longitude } = favoriteLocation?.Location?.Locality || {};

                    const mapPageParams = `center=${Latitude},${Longitude},${defaultMapZoom}&layerId=${defaultMapLayerId}`;
                    const locUrlWithParams = pageId === 'maps' 
                        ? `${locationRouteString}/?${mapPageParams}` 
                        : locationRouteString;
                    const updatedRouteString = stationRouteString ? `${locUrlWithParams}&station=${stationRouteString}` : locUrlWithParams;
                    const url  = getLocationUrl(routePath, updatedRouteString);

                    return <ListItem key={favoriteLocation.Id}>
                        <ListItemLink href={url}>
                            <FavoriteLocationIcon isFavoriteLocation={true} />
                            <DisplayNameContainer>
                                {favoriteLocation?.Location?.Locality?.DisplayCompositeName}
                                <StationContainer>
                                    {favoriteLocation?.Location?.Station?.StationName ?? defaultStationName}
                                </StationContainer>
                            </DisplayNameContainer>
                            <LocationAlert
                                latitude={favoriteLocation?.Location?.Locality?.Latitude}
                                longitude={favoriteLocation?.Location?.Locality?.Longitude}
                                emLocationId={favoriteLocation?.Location?.Id}
                            />
                        </ListItemLink>
                    </ListItem>;
                })}
            </LocationsList>
            <ManageLocationsButton onClick={manageLocationsClick}>
                <GearIcon />
                <ManageLocationsText>
                    {translate('manageLocations')}
                </ManageLocationsText>
            </ManageLocationsButton>
        </Container>
    );
});

FavoriteLocationDropdown.displayName = 'FavoriteLocationDropdown';

FavoriteLocationDropdown.propTypes = {
    favoriteLocations: PropTypes.array,
    routePath: PropTypes.string,
    close: PropTypes.func.isRequired,
    defaultMapLayerId: PropTypes.string
};

const Container = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    top: 56px;
    right: 0;
    z-index: 99;
    width: 100%;
`;

const Header = styled.div`
    padding: 6px 15px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.25;
    text-transform: uppercase;
    color: #FFFFFF;
    background-color: #2f6f8c;
`;

const LocationsList = styled.ul`
    background-color: #FFFFFF;
    display: flex;
    flex-direction: column;
    max-height: 335px;
    height: auto;
    overflow-y: auto;
    list-style: none;
    padding: 0;
`;

const ListItem = styled.li`
    padding-left: 10px;
    cursor: pointer;

    &:hover {
        background-color: rgba(47,111,140,0.05)
    }
`;

const ListItemLink = styled.a`
    display: flex;
    align-items: center;
    color: #435560;
    font-size: 16px;
    font-weight: 400;
    text-overflow: ellipsis;
    overflow: hidden;
    text-decoration: none;
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(216,216,216,0.5);
`;

const DisplayNameContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 70%;
    margin-left: 10px;
`;

const StationContainer = styled.span`
    font-size: 11px;
    font-weight: 400;
    color: rgba(136,136,136,0.8);
`;

const ManageLocationsButton = styled.a`
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 400;
    text-transform: uppercase;
    padding: 0px;
    padding-left: 10px;
    padding-top: 12px;
    padding-bottom: 12px;
    color: #FFFFFF;
    background-color: #2f6f8c;
    cursor: pointer;
`;

const ManageLocationsText = styled.span`
    margin-left: 8px;
`;
